import { useModelStore, useUserStore } from "src/store";
import { ObjectInfos } from "./objects";
import { View } from "./views";
import { Dashboard } from "./Dashboard";

interface User {
    _key: string;
    username: string;
    firstname: string;
    lastname: string;
    owner: boolean;
    active: boolean;
    order: number;
    rights: Right[] | undefined;
}

export interface UpdateUserRequest {
    userKey: string;
    firstname: string;
    lastname: string;
    order: number;
    active: boolean;
    owner: boolean;
    rights: Right[] | undefined;
}

export interface UserRequest {
    userKey: string;
    username: string;
}

export interface NewUserRequest {
    firstname: string;
    lastname: string;
    email: string;
    username: string;
    tenant: string;
    order: number;
    sendVerifEmail: boolean;
}

/** Access level of a right: NONE < READ < EDIT < FULL (FULL = configuration). */
export type RightLevel = "FULL" | "EDIT" | "READ" | "NONE"
/**
 * Legacy access values. Before the "level + options" split, the access mixed
 * the level and the add / delete options in a single value. They are still
 * accepted on read (stored roles, API answers) and converted by normalizeRight
 * into a level and an options list; new code never writes them.
 * @deprecated use a RightLevel and Right.options
 */
export type LegacyRightAccess = "FULL_WITH_DELETE" | "FULL_WITH_ADD" | "EDIT_WITH_DELETE" | "EDIT_WITH_ADD" | "DELETE"
export type RightAccess = RightLevel | LegacyRightAccess
/** @deprecated legacy value, see LegacyRightAccess */
export const FullWithDeleteRightAccess: RightAccess = "FULL_WITH_DELETE"
/** @deprecated legacy value, see LegacyRightAccess */
export const FullWithAddRightAccess: RightAccess = "FULL_WITH_ADD"
export const FullRightAccess: RightLevel = "FULL"
/** @deprecated legacy value, see LegacyRightAccess */
export const EditWithDeleteRightAccess: RightAccess = "EDIT_WITH_DELETE"
/** @deprecated legacy value, see LegacyRightAccess */
export const EditWithAddRightAccess: RightAccess = "EDIT_WITH_ADD"
/** @deprecated legacy value, see LegacyRightAccess */
export const DeleteRightAccess: RightAccess = "DELETE"
export const EditRightAccess: RightLevel = "EDIT"
export const ReadRightAccess: RightLevel = "READ"
export const NoneRightAccess: RightLevel = "NONE"
/** Access levels in ascending order: the position of a level is its rank. */
export const AllRightLevels: RightLevel[] = [NoneRightAccess, ReadRightAccess, EditRightAccess, FullRightAccess]
/** @deprecated use AllRightLevels (ascending order) or levelIndex */
export const AllRightAccess: RightAccess[] = [FullRightAccess, EditRightAccess, ReadRightAccess, NoneRightAccess]

/** Extra capability granted on top of an access level. */
export type RightOption = "ADD" | "DELETE" | "EXPORT"
/** Create records (requires the EDIT level). */
export const AddRightOption: RightOption = "ADD"
/** Delete records (requires the EDIT level). */
export const DeleteRightOption: RightOption = "DELETE"
/** Export records (requires the READ level). */
export const ExportRightOption: RightOption = "EXPORT"
/** Options in their canonical order. */
export const AllRightOptions: RightOption[] = [AddRightOption, DeleteRightOption, ExportRightOption]
/**
 * Single truth table of the coherence rule between a level and its options: an
 * option is only meaningful, and only effective, when the level of the right is
 * at least the listed one. The role editor, the back-end validation and the
 * effective checks (hasGlobalRightOption) all rely on it.
 */
export const OPTION_MIN_LEVEL: Record<RightOption, RightLevel> = {
    ADD: EditRightAccess,
    DELETE: EditRightAccess,
    EXPORT: ReadRightAccess,
}

/** What every legacy access value stands for ("..._WITH_DELETE" always implied the add option). */
const LEGACY_ACCESSES: Record<LegacyRightAccess, { level: RightLevel; options: RightOption[] }> = {
    FULL_WITH_DELETE: { level: FullRightAccess, options: [AddRightOption, DeleteRightOption] },
    FULL_WITH_ADD: { level: FullRightAccess, options: [AddRightOption] },
    EDIT_WITH_DELETE: { level: EditRightAccess, options: [AddRightOption, DeleteRightOption] },
    EDIT_WITH_ADD: { level: EditRightAccess, options: [AddRightOption] },
    DELETE: { level: EditRightAccess, options: [AddRightOption, DeleteRightOption] },
}


export type RightType = "OBJECT" | "FIELD" | "VIEW" | "DASHBOARD" | "MEMBERS" | "DRIVE"
export const ObjectRightType: RightType = "OBJECT"
export const FieldRightType: RightType = "FIELD"
export const ViewRightType: RightType = "VIEW"
export const DashboardRightType: RightType = "DASHBOARD"
export const MembersRightType: RightType = "MEMBERS"
export const DriveRightType: RightType = "DRIVE"

export function hasRoleRightAccess(role: Role | null, rightType: RightType, rightAccess: RightAccess, recordKey: string | null = null, onlyRightOnKeys: boolean = false): boolean {
    if (!role) {
        return false;
    }
    if (role.isOwner) {
        return true;
    }
    return hasRightAccess(role.rights ?? [], rightType, rightAccess, recordKey, onlyRightOnKeys);
}

export function hasUserRightAccess(user: User | null, rightType: RightType, rightAccess: RightAccess, recordKey: string | null = null, onlyRightOnKeys: boolean = false): boolean {
    if (!user) {
        return false;
    }
    if (user.owner) {
        return true;
    }
    return hasRightAccess(user.rights ?? [], rightType, rightAccess, recordKey, onlyRightOnKeys);
}

export function hasGlobalRightAccess(user: User | undefined, userRole: Role | null, rightType: RightType, rightAccess: RightAccess, recordKey: string | null = null, onlyRightOnKeys: boolean = false): boolean {
    
    if (user?.owner || userRole?.isOwner) {
        return true;
    }
    
    // const 
    const userRight = getAccessRight(user?.rights ?? [], rightType, recordKey, onlyRightOnKeys);

    if (userRight) {
        return hasAccess(userRight, rightAccess);
    }

    const userRoleRight = getAccessRight(userRole?.rights ?? [], rightType, recordKey, onlyRightOnKeys);
    if (userRoleRight) {
        return hasAccess(userRoleRight, rightAccess);
    }

    return false;
}

/** The right that applies to a record among the rights of a type: the record-scoped one, else the default one unless onlyRightOnKeys. */
export function getAccessRight(rights: Right[], rightType: RightType, recordKey: string | null = null, onlyRightOnKeys: boolean = false): Right | null {
    const filteredRights = rights.filter(right => {
        if (!right.active) {
            return false;
        }
        if (right.type === rightType) {
            if (right.recordKey === null || right.recordKey === "") {
                return true;
            } else if (recordKey) {
                return right.recordKey === recordKey;
            }
            return false;
        }
        return false;
    });
    let right = filteredRights.find(right => right.type === rightType && right.recordKey === recordKey);
    if (!right && !onlyRightOnKeys) {
        right = filteredRights.find(right => right.type === rightType && right.recordKey === null);
    }
    return right ?? null;
}

export function hasRightAccess(rights: Right[], rightType: RightType, rightAccess: RightAccess, recordKey: string | null = null, onlyRightOnKeys: boolean = false): boolean {

    const right = getAccessRight(rights, rightType, recordKey, onlyRightOnKeys);
    return hasAccess(right, rightAccess);
}

/**
 * Tells whether a right satisfies a target access: its level must be at least
 * the level of the target and it must effectively hold every option the target
 * implies (a legacy target such as EDIT_WITH_ADD keeps working). An unknown
 * current or target value never grants anything.
 */
export function hasAccess(right: Right | null, rightAccess: RightAccess): boolean {
    if (!right) {
        return false;
    }
    const target = normalizeAccess(rightAccess);
    if (!target) {
        return false;
    }
    const current = normalizeRight(right);
    const currentIndex = levelIndex(current.access);
    if (currentIndex < 0 || currentIndex < levelIndex(target.level)) {
        return false;
    }
    return target.options.every(option => hasOption(current, option));
}

/** Rank of an access level (0 for NONE up to 3 for FULL), -1 for anything else (legacy values included: normalise first). */
export function levelIndex(access: string | null | undefined): number {
    return AllRightLevels.indexOf(access as RightLevel);
}

/** Splits an access value into its level and the options it implies; null for a value that is neither a level nor a legacy value. */
export function normalizeAccess(access: string | null | undefined): { level: RightLevel; options: RightOption[] } | null {
    if (access && Object.prototype.hasOwnProperty.call(LEGACY_ACCESSES, access)) {
        const legacy = LEGACY_ACCESSES[access as LegacyRightAccess];
        return { level: legacy.level, options: [...legacy.options] };
    }
    if (levelIndex(access) >= 0) {
        return { level: access as RightLevel, options: [] };
    }
    return null;
}

/** Union of both lists: known options first in the canonical order, unknown ones last, no duplicate. Always a new array. */
function mergeOptions(options: RightOption[], implied: RightOption[]): RightOption[] {
    const all = [...options, ...implied];
    const merged = AllRightOptions.filter(option => all.includes(option));
    for (const option of all) {
        if (!AllRightOptions.includes(option) && !merged.includes(option)) {
            merged.push(option);
        }
    }
    return merged;
}

/**
 * Returns a copy of the right where a legacy access value is converted into its
 * level and the options it implies are merged into the options of the right
 * (deduplicated, canonical order, always an array). Idempotent.
 */
export function normalizeRight(right: Right): Right {
    const normalized = normalizeAccess(right.access);
    return {
        ...right,
        access: normalized ? normalized.level : right.access,
        options: mergeOptions(right.options ?? [], normalized?.options ?? []),
    };
}

export function normalizeRole(role: Role): Role {
    return { ...role, rights: (role.rights ?? []).map(normalizeRight) };
}

export function normalizeUser(user: User): User {
    return user.rights ? { ...user, rights: user.rights.map(normalizeRight) } : user;
}

/** Tells whether an option is meaningful for an access level (see OPTION_MIN_LEVEL). Unknown option or level: false. */
export function isOptionAllowed(option: RightOption, level: RightAccess | string | null | undefined): boolean {
    const minLevel = OPTION_MIN_LEVEL[option];
    if (!minLevel) {
        return false;
    }
    const index = levelIndex(level);
    return index >= 0 && index >= levelIndex(minLevel);
}

/** Keeps only the options allowed by the level. */
export function allowedOptions(level: RightAccess, options: RightOption[]): RightOption[] {
    return options.filter(option => isOptionAllowed(option, level));
}

/**
 * Options written for a right that has no options UI (dashboards, members,
 * views, drive rows of the role editor, view sharing): the EDIT and FULL levels
 * carry the add and delete options, as they implicitly did before the split.
 */
export function implicitOptionsForLevel(access: RightAccess): RightOption[] {
    const level = normalizeAccess(access)?.level;
    return level && levelIndex(level) >= levelIndex(EditRightAccess) ? [AddRightOption, DeleteRightOption] : [];
}

/**
 * Options of a right after a level change in the role editor:
 * - climbing from NONE / READ to EDIT / FULL turns add and delete on;
 * - export is never turned on by a level change, but is kept when it was already checked;
 * - options that the new level does not allow are dropped.
 */
export function nextOptions(prevLevel: RightLevel, nextLevel: RightLevel, prevOptions: RightOption[]): RightOption[] {
    const editIndex = levelIndex(EditRightAccess);
    const climbsToEdit = levelIndex(nextLevel) >= editIndex && levelIndex(prevLevel) < editIndex;
    const base = climbsToEdit ? [AddRightOption, DeleteRightOption] : prevOptions.filter(option => option !== ExportRightOption);
    const keepExport = prevOptions.includes(ExportRightOption);
    return allowedOptions(nextLevel, mergeOptions(base, keepExport ? [ExportRightOption] : []));
}

/** Tells whether a right effectively grants an option: present AND allowed by the level of the right. */
export function hasOption(right: Right | null, option: RightOption): boolean {
    if (!right) {
        return false;
    }
    const current = normalizeRight(right);
    return isOptionAllowed(option, current.access) && (current.options ?? []).includes(option);
}

/** hasGlobalRightAccess for an option: owner bypass, then the user right (final when present), then the role right. */
export function hasGlobalRightOption(user: User | undefined, userRole: Role | null, rightType: RightType, option: RightOption, recordKey: string | null = null, onlyRightOnKeys: boolean = false): boolean {
    if (user?.owner || userRole?.isOwner) {
        return true;
    }
    const userRight = getAccessRight(user?.rights ?? [], rightType, recordKey, onlyRightOnKeys);
    if (userRight) {
        return hasOption(userRight, option);
    }
    const userRoleRight = getAccessRight(userRole?.rights ?? [], rightType, recordKey, onlyRightOnKeys);
    if (userRoleRight) {
        return hasOption(userRoleRight, option);
    }
    return false;
}

export function checkedSelection(selectedObjectInfos: ObjectInfos | null, selectedView: View | null, selectedDashboard: Dashboard | null): { selectedObjectInfos: ObjectInfos | null, selectedView: View | null, selectedDashboard: Dashboard | null } {
    const userRole = useModelStore.getState().userRole;
    const user = useUserStore.getState().user;
    if (!userRole || !user) {
        return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
    }
    if (user.owner) {
        return ({ selectedObjectInfos: selectedObjectInfos, selectedView: selectedView, selectedDashboard: selectedDashboard })
    }
    if (selectedObjectInfos) {
        const hasReadAccessToObject = hasGlobalRightAccess(user, userRole, ObjectRightType, ReadRightAccess, selectedObjectInfos.object._key);
        if (!hasReadAccessToObject) {
            return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
        }
        if (selectedView) {
            const hasReadAccessToView = user._key === selectedView.ownerKey || hasGlobalRightAccess(user, userRole, ViewRightType, ReadRightAccess, selectedView._key);
            if (!hasReadAccessToView) {
                return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
            }
        } else {
            const hasFullAccessToObject = hasGlobalRightAccess(user, userRole, ObjectRightType, FullRightAccess, selectedObjectInfos.object._key);
            if (!hasFullAccessToObject) {
                return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
            }
        }
        return ({ selectedObjectInfos: selectedObjectInfos, selectedView: selectedView, selectedDashboard: null })
    } else if (selectedDashboard) {
        const hasReadAccessToDashboard = !selectedDashboard.ownerKey || selectedDashboard.ownerKey === user._key || hasGlobalRightAccess(user, userRole, DashboardRightType, ReadRightAccess, selectedDashboard._key, true);
        if (!hasReadAccessToDashboard) {
            return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
        }
        return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: selectedDashboard })
    }
    return ({ selectedObjectInfos: null, selectedView: null, selectedDashboard: null })
}

/**
 * One right of a role or of a user: an access level on a right type, optionally
 * scoped on a record (recordKey), plus the options granted on top of the level.
 * A right without record key is the default right of its type. The options are
 * optional on the wire (older payloads); a normalised right always has them.
 */
export interface Right {
    type: RightType,
    access: RightAccess,
    options?: RightOption[],
    recordKey: string | null
    active: boolean
}

export type Role = {
    _key: string,
    order: number,
    name: string,
    rights: Right[]
    isOwner: boolean
}

/** Deep copy of a role: editing the options of the copy never touches the original. */
export function copyRole(role: Role): Role {
    return { ...role, rights: role.rights.map(right => ({ ...right, options: [...(right.options ?? [])] })) };
}

export function newRole(isOwner: boolean = false): Role {
    return {
        _key: "",
        order: 0,
        name: "",
        rights: [],
        isOwner: isOwner
    };
}

export interface UpdateUserRole {
    WorkspaceKey: string;
    UserKey: string;
    RoleKey: string;
}

export type GetRoleRequest = {
    rightType: RightType | null,
    recordKey: string | null
}

export default User;